import React, { useEffect, useState } from 'react';
import { Modal, Button, Form } from 'react-bootstrap';
import Select from 'react-select';
import axios from 'axios';
import config from '../../../../../config';
import { toast } from 'react-toastify';

function MaterialEditModal({ show, onHide, buyerId, materials = [], onUpdated }) {
  const [productOptions, setProductOptions] = useState([]);
  const [packagingOptions, setPackagingOptions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [selectedPackaging, setSelectedPackaging] = useState([]);
  const [saving, setSaving] = useState(false);

  // Load product and packaging material lists from API
  useEffect(() => {
    const fetchProducts = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/products`);
        const options = res.data.map(p => ({
          label: p.productName,
          value: p._id,
        }));
        setProductOptions(options);
      } catch (err) {
        console.error('Failed to load products', err);
        toast.error('Failed to load products');
      }
    };

    const fetchPackagingMaterials = async () => {
      try {
        const res = await axios.get(`${config.API_BASE_URL}/packing-materials`);
        const options = res.data.map(p => ({
          label: `${p.name} (${p.alias})`,
          value: p._id,
        }));
        setPackagingOptions(options);
      } catch (err) {
        console.error('Failed to load packaging materials', err);
        toast.error('Failed to load packaging materials');
      }
    };

    fetchProducts();
    fetchPackagingMaterials();
  }, []);

  // Map incoming materials to selected options
  useEffect(() => {
    if (materials.length && (productOptions.length || packagingOptions.length)) {
      const products = [];
      const packaging = [];

      materials.forEach(material => {
        if (material.material_type === 'Product') {
          const option = productOptions.find(opt => opt.value === material.material_id._id);
          if (option) products.push(option);
        } else if (material.material_type === 'PackingMaterial') {
          const option = packagingOptions.find(opt => opt.value === material.material_id._id);
          if (option) packaging.push(option);
        }
      });

      setSelectedProducts(products);
      setSelectedPackaging(packaging);
    }
  }, [materials, productOptions, packagingOptions]);

  const handleProductChange = (selected) => {
    setSelectedProducts(selected || []);
  };

  const handlePackagingChange = (selected) => {
    setSelectedPackaging(selected || []);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      // Combine products and packaging materials into the new format
      const materialSupplied = [
        ...selectedProducts.map(product => ({
          material_id: product.value,
          material_type: 'Product'
        })),
        ...selectedPackaging.map(packaging => ({
          material_id: packaging.value,
          material_type: 'PackingMaterial'
        }))
      ];

      await axios.put(`${config.API_BASE_URL}/buyers/${buyerId}`, {
        material_supplied: materialSupplied,
      });
      toast.success('Materials updated');
      onUpdated();
      onHide();
    } catch (err) {
      console.error('Failed to update materials', err);
      toast.error('Failed to update materials');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal show={show} onHide={onHide} size="lg">
      <Modal.Header closeButton>
        <Modal.Title>Edit Products Bought By Buyer</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <Form onSubmit={handleSubmit}>
          <Form.Group className="mb-3 fs-9">
            <Form.Label>Products</Form.Label>
            <Select
              isMulti
              options={productOptions}
              value={selectedProducts}
              onChange={handleProductChange}
              placeholder="Select products..."
              classNamePrefix="react-select"
            />
          </Form.Group>

          <Form.Group className="mb-3 fs-9">
            <Form.Label>Packaging Materials</Form.Label>
            <Select
              isMulti
              options={packagingOptions}
              value={selectedPackaging}
              onChange={handlePackagingChange}
              placeholder="Select packaging materials..."
              classNamePrefix="react-select"
            />
          </Form.Group>

          <Button type="submit" variant="primary" disabled={saving}>
            {saving ? 'Saving...' : 'Save'}
          </Button>
        </Form>
      </Modal.Body>
    </Modal>
  );
}

export default MaterialEditModal;
